import React, { useEffect, useState } from 'react';
import { Card, Form, Table, Spinner, Alert, Button, Row, Col, Badge, Modal } from 'react-bootstrap';
import { adminApi, extractError } from '../api/client';
import { toast } from 'sonner';

/**
 * Election Dates page — read-only browser for the `county_election_dates`
 * collection (the calendar half of the contests-vs-calendar split).
 *
 * Contest-bearing events live on the existing "Election Days" page;
 * pure calendar entries (date + place + deadlines, no contests) are
 * surfaced here. Sources: AI Scrape (FL DOE calendar) + manual.
 */
export default function ElectionDatesPage() {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [stateId, setStateId] = useState('');
  const [countyId, setCountyId] = useState('');
  const [year, setYear] = useState('');
  const [type, setType] = useState('');
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const pageSize = 25;

  // Initial state list
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await adminApi.get('/admin/states', { params: { page_size: 200 } });
        if (active) setStates(r.data.items || r.data || []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { active = false; };
  }, []);

  // Counties for selected state
  useEffect(() => {
    if (!stateId) { setCounties([]); return; }
    let active = true;
    (async () => {
      try {
        const r = await adminApi.get(`/admin/states/${stateId}/counties`, { params: { page_size: 300 } });
        if (active) setCounties(r.data.items || r.data || []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { active = false; };
  }, [stateId]);

  // Main listing
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    (async () => {
      try {
        const params = { page, page_size: pageSize, upcoming_only: upcomingOnly, sort: 'election_date', order: 'asc' };
        if (stateId) params.state_id = stateId;
        if (countyId) params.county_id = countyId;
        if (year) params.year = Number(year);
        if (type) params.type = type;
        if (q.trim()) params.q = q.trim();
        const r = await adminApi.get('/admin/election-dates', { params });
        if (!active) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, stateId, countyId, year, type, upcomingOnly, q]);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admin/election-dates/${deleteTarget.id}`);
      toast.success(`Deleted ${deleteTarget.election_name}`);
      setDeleteTarget(null);
      // refetch
      setPage((p) => p);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      toast.error(extractError(e));
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-4" data-testid="election-dates-page">
      <div className="d-flex align-items-center mb-3 gap-2">
        <h1 className="h4 mb-0">Election Dates</h1>
        <Badge bg="info" pill>Calendar</Badge>
      </div>
      <p className="text-muted small">
        Pure calendar entries — date + place + deadlines, no contests attached.
        Contest-bearing events live on the <strong>Election Days</strong> page.
        Most rows here come from AI scrape (FL DOE calendar) or manual entry.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col xs={12} md={3}>
              <Form.Label className="small mb-1">State</Form.Label>
              <Form.Select value={stateId} onChange={(e) => { setPage(1); setStateId(e.target.value); setCountyId(''); }} data-testid="filter-state">
                <option value="">All states</option>
                {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small mb-1">County</Form.Label>
              <Form.Select value={countyId} onChange={(e) => { setPage(1); setCountyId(e.target.value); }} disabled={!stateId} data-testid="filter-county">
                <option value="">All / statewide</option>
                {counties.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Year</Form.Label>
              <Form.Control type="number" placeholder="2026" value={year} onChange={(e) => { setPage(1); setYear(e.target.value); }} data-testid="filter-year" />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Type</Form.Label>
              <Form.Select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} data-testid="filter-type">
                <option value="">All types</option>
                <option value="general">General</option>
                <option value="primary">Primary</option>
                <option value="runoff">Runoff</option>
                <option value="special">Special</option>
                <option value="municipal">Municipal</option>
                <option value="school">School</option>
                <option value="other">Other</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2} className="d-flex align-items-end">
              <Form.Check
                type="switch" id="upcoming-only-switch"
                label="Upcoming only" checked={upcomingOnly}
                onChange={(e) => { setPage(1); setUpcomingOnly(e.target.checked); }}
                data-testid="filter-upcoming-only"
              />
            </Col>
            <Col xs={12}>
              <Form.Control type="search" placeholder="Search by name or notes…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} data-testid="filter-q" />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 text-center"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="p-5 text-center text-muted">
              No calendar entries match these filters.
            </div>
          ) : (
            <Table hover responsive size="sm" className="mb-0" data-testid="election-dates-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>State</th>
                  <th>County</th>
                  <th>Source</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} data-testid={`election-date-row-${d.id}`}>
                    <td className="text-muted">{d.election_date}</td>
                    <td className="fw-semibold">{d.election_name}</td>
                    <td><Badge bg="secondary-subtle" text="dark">{d.election_type || '—'}</Badge></td>
                    <td>{d._state?.name || '—'}</td>
                    <td>{d._county?.name || <span className="text-muted">statewide</span>}</td>
                    <td className="text-muted small" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(d.created_by || '').startsWith('ai_') ? <Badge bg="warning-subtle" text="dark">AI scrape</Badge> : (d.created_by || '—')}
                    </td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-secondary" onClick={() => setViewing(d)} data-testid={`view-${d.id}`}>View</Button>{' '}
                      <Button size="sm" variant="outline-danger" onClick={() => setDeleteTarget(d)} data-testid={`delete-${d.id}`}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">Page {page} of {totalPages} — {total} total</span>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <Modal show={!!viewing} onHide={() => setViewing(null)} size="lg" centered data-testid="election-date-view-modal">
        <Modal.Header closeButton><Modal.Title>{viewing?.election_name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {viewing && (
            <Row className="g-2 small">
              <Col xs={6}><strong>Date:</strong> {viewing.election_date}</Col>
              <Col xs={6}><strong>Type:</strong> {viewing.election_type}</Col>
              <Col xs={6}><strong>Year:</strong> {viewing.year || '—'}</Col>
              <Col xs={6}><strong>State:</strong> {viewing._state?.name || '—'}</Col>
              <Col xs={6}><strong>County:</strong> {viewing._county?.name || 'statewide'}</Col>
              <Col xs={6}><strong>Status:</strong> {viewing.status || '—'}</Col>
              <hr className="my-2" />
              <Col xs={6}><strong>Voter registration:</strong> {viewing.voter_registration_deadline || '—'}</Col>
              <Col xs={6}><strong>Mail-in request:</strong> {viewing.mail_in_request_deadline || '—'}</Col>
              <Col xs={6}><strong>Mail-in return:</strong> {viewing.mail_in_return_deadline || '—'}</Col>
              <Col xs={6}><strong>Early voting:</strong> {viewing.early_voting_start || '—'}{viewing.early_voting_end ? ` → ${viewing.early_voting_end}` : ''}</Col>
              <hr className="my-2" />
              <Col xs={12}><strong>Source URL:</strong> {viewing.source_url ? <a href={viewing.source_url} target="_blank" rel="noreferrer">{viewing.source_url}</a> : '—'}</Col>
              <Col xs={12}><strong>Notes:</strong> {viewing.notes || '—'}</Col>
              <Col xs={12} className="text-muted small mt-2">Created by: {viewing.created_by || '—'}</Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="primary" onClick={() => setViewing(null)}>Close</Button></Modal.Footer>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered data-testid="election-date-delete-modal">
        <Modal.Header closeButton><Modal.Title>Delete calendar entry</Modal.Title></Modal.Header>
        <Modal.Body>Delete <strong>{deleteTarget?.election_name}</strong> ({deleteTarget?.election_date})?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} data-testid="election-date-delete-confirm">Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
